import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook that intercepts browser back button when an overlay (e.g. iframe) is open.
 * - When `isOpen` becomes true, pushes a history entry.
 * - Pressing browser back pops that entry and calls `onClose`.
 * - Call the returned `close` function from UI dismiss buttons instead of calling onClose directly.
 */
export function useOverlayBackHandler(isOpen: boolean, onClose: () => void) {
  const pushedRef = useRef(false);

  // Push history entry when overlay opens
  useEffect(() => {
    if (isOpen && !pushedRef.current) {
      window.history.pushState({ overlayOpen: true }, '', window.location.href);
      pushedRef.current = true;
    }
  }, [isOpen]);

  // Listen for popstate (browser back)
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePopState = () => {
      pushedRef.current = false;
      onClose();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, onClose]);

  // Programmatic close: mark as handled immediately to prevent double history.back(),
  // then pop the history entry we pushed which triggers popstate -> onClose
  const close = useCallback(() => {
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    } else {
      onClose();
    }
  }, [onClose]);

  return close;
}
